import { FC, useMemo } from 'react'
import {
  AI_LISTENING_FOCUS_FILTER_TABS,
  InsightCategory,
  SmartInsight,
  SmartInsightsAIRecommendations,
} from 'src/charts/SmartInsightsAIRecommendations'
import { useGetAiListeningSmartInsightsQuery } from 'src/reactQuery/useGetAiListeningSmartInsightsQuery'
import { InsightFocus } from 'src/services/aiListeningApi/brandProfile.types'
import styles from './deepDive.module.scss'

type InsightWithFocus = SmartInsight & { focus?: InsightFocus }

// Insights stored before `focus` existed carry only the legacy category; the
// backfill maps them the same way (spec 3.5).
const LEGACY_CATEGORY_FOCUS: Partial<Record<string, InsightFocus>> = {
  brand_health_signals: 'visibility',
  competitor_moves: 'competitive',
  content_recommendations: 'content',
  crisis_warnings: 'perception',
  influencer_opportunities: 'citation',
}

const focusOf = (insight: InsightWithFocus): InsightFocus =>
  insight.focus ??
  (insight.actionType === 'technical'
    ? 'technical_aeo'
    : LEGACY_CATEGORY_FOCUS[insight.category] ?? 'visibility')

interface DeepDiveInsightsProps {
  checkedAt?: string
  promptId: string
}

// Smart Insights & AI Recommendations, filtered by focus (Visibility,
// Competitive, Citation, Content, Technical AEO, Perception).
export const DeepDiveInsights: FC<DeepDiveInsightsProps> = ({
  checkedAt,
  promptId,
}) => {
  const { data, isFetching } = useGetAiListeningSmartInsightsQuery(
    promptId,
    checkedAt
  )

  const insights = useMemo(
    () =>
      ((data?.data?.insights ?? []) as InsightWithFocus[]).map((insight) => ({
        ...insight,
        category: focusOf(insight) as InsightCategory,
      })),
    [data]
  )

  return (
    <SmartInsightsAIRecommendations
      className={styles.insights}
      data={
        data?.data
          ? { hasInsights: data.data.hasInsights ?? false, insights }
          : undefined
      }
      filterTabs={AI_LISTENING_FOCUS_FILTER_TABS}
      homePageVariant
      isLoading={isFetching}
      modalMetricsVariant="ai"
      modalSourceDate={checkedAt}
    />
  )
}
